import { useEffect, useCallback, useRef } from 'react';
import logger from '../utils/logger';

/**
 * グローバルキーボードイベント管理フック
 * 複数のコンポーネントのキーボードイベントを優先順位に基づいて統一管理
 */

interface KeyboardEventHandler {
  id: string;
  priority: number; // 数値が小さいほど高優先度
  handler: (event: KeyboardEvent) => boolean; // true = イベント処理済み（他のハンドラーを実行しない）
  shouldIgnoreInputFocus?: boolean; // true = 入力フィールドフォーカス時も実行
  keys?: string[]; // 対象キー（指定されていれば該当キーのみ処理）
}

interface UseGlobalKeyboardOptions {
  enabled?: boolean;
  debugMode?: boolean;
}

interface UseGlobalKeyboardReturn {
  registerHandler: (handler: KeyboardEventHandler) => () => void;
  unregisterHandler: (id: string) => void;
  getActiveHandlers: () => KeyboardEventHandler[];
  isInputFocused: () => boolean;
}

// グローバル状態（シングルトンパターン）
class GlobalKeyboardManager {
  private handlers: Map<string, KeyboardEventHandler> = new Map();
  private isListenerAttached = false;
  private debugMode = false;

  private isInputElementFocused(): boolean {
    const activeElement = document.activeElement;
    if (!activeElement) return false;

    const isInput =
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT' ||
      (activeElement as HTMLElement).contentEditable === 'true';

    return isInput;
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    const isInputFocused = this.isInputElementFocused();

    if (this.debugMode) {
      logger.info('[useGlobalKeyboard]', {
        key: event.key,
        isInputFocused,
        activeElement: document.activeElement?.tagName,
        handlersCount: this.handlers.size,
      });
    }

    // ハンドラーを優先順位順にソート
    const sortedHandlers = Array.from(this.handlers.values()).sort(
      (a, b) => a.priority - b.priority
    );

    for (const handler of sortedHandlers) {
      // 入力フィールドフォーカス時のスキップ判定
      if (isInputFocused && !handler.shouldIgnoreInputFocus) {
        continue;
      }

      // 対象キーの判定
      if (handler.keys && !handler.keys.includes(event.key)) {
        continue;
      }

      try {
        const handled = handler.handler(event);
        if (handled) {
          if (this.debugMode) {
            logger.info('[useGlobalKeyboard] Event handled by:', handler.id);
          }
          break; // 処理済みの場合、後続のハンドラーを実行しない
        }
      } catch (error) {
        logger.error(`[useGlobalKeyboard] Error in handler ${handler.id}:`, error);
      }
    }
  };

  private ensureListener(): void {
    if (!this.isListenerAttached) {
      document.addEventListener('keydown', this.handleKeyDown, true); // capture phase
      this.isListenerAttached = true;
    }
  }

  private removeListener(): void {
    if (this.isListenerAttached && this.handlers.size === 0) {
      document.removeEventListener('keydown', this.handleKeyDown, true);
      this.isListenerAttached = false;
    }
  }

  registerHandler(handler: KeyboardEventHandler): () => void {
    this.handlers.set(handler.id, handler);
    this.ensureListener();

    // unregister function を返す
    return () => {
      this.unregisterHandler(handler.id);
    };
  }

  unregisterHandler(id: string): void {
    this.handlers.delete(id);
    this.removeListener();
  }

  getActiveHandlers(): KeyboardEventHandler[] {
    return Array.from(this.handlers.values()).sort((a, b) => a.priority - b.priority);
  }

  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  isInputFocused(): boolean {
    return this.isInputElementFocused();
  }
}

// シングルトンインスタンス
const globalKeyboardManager = new GlobalKeyboardManager();

export const useGlobalKeyboard = ({
  enabled = true,
  debugMode = false,
}: UseGlobalKeyboardOptions = {}): UseGlobalKeyboardReturn => {
  const handlersRef = useRef<Set<string>>(new Set());

  // デバッグモードの設定
  useEffect(() => {
    globalKeyboardManager.setDebugMode(debugMode);
  }, [debugMode]);

  const registerHandler = useCallback(
    (handler: KeyboardEventHandler): (() => void) => {
      if (!enabled) {
        return () => {}; // no-op
      }

      handlersRef.current.add(handler.id);
      return globalKeyboardManager.registerHandler(handler);
    },
    [enabled]
  );

  const unregisterHandler = useCallback((id: string): void => {
    handlersRef.current.delete(id);
    globalKeyboardManager.unregisterHandler(id);
  }, []);

  const getActiveHandlers = useCallback((): KeyboardEventHandler[] => {
    return globalKeyboardManager.getActiveHandlers();
  }, []);

  const isInputFocused = useCallback((): boolean => {
    return globalKeyboardManager.isInputFocused();
  }, []);

  // クリーンアップ
  useEffect(() => {
    return () => {
      // このフックで登録したハンドラーを全て削除
      handlersRef.current.forEach(id => {
        globalKeyboardManager.unregisterHandler(id);
      });
      handlersRef.current.clear();
    };
  }, []);

  return {
    registerHandler,
    unregisterHandler,
    getActiveHandlers,
    isInputFocused,
  };
};

// 便利な定数
export const KEYBOARD_PRIORITIES = {
  MODAL: 10, // モーダル（最高優先度）
  DROPDOWN: 20, // ドロップダウン
  FORM_SHORTCUTS: 30, // フォームのショートカット
  PAGE_SHORTCUTS: 40, // ページレベルのショートカット
  GLOBAL_SHORTCUTS: 50, // グローバルショートカット（最低優先度）
} as const;

export default useGlobalKeyboard;
